import { memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { IoMdCloseCircleOutline } from 'react-icons/io';
import { SelectItem, Selection, TmdxSelect } from '@ds';
import { LATERALITY_OPTIONS } from '@features/components/CaseFormBlocks/config';
import { CaseFormType } from '@features/forms/CaseForms/schema';
import { FormRow } from '@features/components/CaseFormBlocks/components';
import { Laterality } from '@/api/apis/case';

type LateralitySelectorProps = {
  disabled?: boolean;
  hasPermission?: boolean;
  disallowUnset?: boolean;
};

export const LateralitySelector = memo(({ disabled, hasPermission, disallowUnset }: LateralitySelectorProps) => {
  const { control } = useFormContext<CaseFormType>();

  const isReadOnly = disabled || !hasPermission;

  return (
    <FormRow title="Laterality">
      <div className="group grow-0 flex-[48.5%] w-auto">
        <Controller
          name="laterality"
          control={control}
          render={({ field: { ref, value, onChange, ...field }, fieldState: { invalid, error } }) => {
            const hasSelection = value !== Laterality.NotDefined;
            const canClear = !isReadOnly && hasSelection && !disallowUnset;

            const handleSelectionChange = (keys: Selection) => {
              if (keys === 'all') {
                return;
              }

              const selectedValue = Array.from(keys)[0];

              if (selectedValue === undefined) {
                return;
              }

              onChange(selectedValue);
            };

            const handleClear = () => {
              onChange(Laterality.NotDefined);
            };

            return (
              <TmdxSelect
                {...field}
                placeholder="Not selected"
                aria-label="Select laterality"
                isDisabled={isReadOnly}
                disallowEmptySelection={false}
                selectedKeys={hasSelection ? new Set([value]) : new Set()}
                isInvalid={invalid}
                errorMessage={error?.message}
                endContent={
                  canClear ? (
                    <button
                      type="button"
                      aria-label="Clear laterality"
                      className="pointer-events-auto flex shrink-0 items-center justify-center opacity-0 transition-opacity group-hover:opacity-100"
                      onPointerDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        handleClear();
                      }}
                    >
                      <IoMdCloseCircleOutline size={22} />
                    </button>
                  ) : undefined
                }
                onSelectionChange={handleSelectionChange}
              >
                {LATERALITY_OPTIONS.map((option) => (
                  <SelectItem key={option.value}>{option.name}</SelectItem>
                ))}
              </TmdxSelect>
            );
          }}
        />
      </div>
    </FormRow>
  );
});
